import { createLeadHandler } from './handler';
import { LeadHandlerConfig, LeadFormData, LeadApiResponse } from '../types';

/**
 * Configuration for BusinessFlow CRM integration
 */
export interface BusinessFlowConfig {
  /** BusinessFlow API base URL (e.g., https://api.businessflow.co.za) */
  apiUrl: string;
  /** BusinessFlow API key for authentication (leads-specific key) */
  apiKey: string;
  /** Source URL/domain for lead attribution (e.g., https://yoursite.com) */
  sourceUrl: string;
  /** reCAPTCHA secret key for server-side verification (optional) */
  recaptchaSecret?: string;
  /** Minimum reCAPTCHA score for v3 (optional, defaults to 0.5) */
  minimumScore?: number;
  /** Custom success callback after lead is submitted to BusinessFlow */
  onSuccess?: (data: LeadFormData, response: LeadApiResponse) => Promise<void>;
  /** Custom error callback if submission fails */
  onError?: (data: LeadFormData, error: Error) => Promise<void>;
}

/**
 * Create a NextJS API route handler that submits leads to BusinessFlow CRM
 *
 * @example
 * ```typescript
 * // app/api/lead/route.ts
 * import { createBusinessFlowHandler } from '@businessflow/leads/server';
 *
 * export const POST = createBusinessFlowHandler({
 *   apiUrl: process.env.BUSINESS_FLOW_API_URL!,
 *   apiKey: process.env.BUSINESS_FLOW_LEADS_API_KEY!,
 *   sourceUrl: process.env.BUSINESS_FLOW_LEADS_DOMAIN!,
 *   recaptchaSecret: process.env.RECAPTCHA_SECRET_KEY,
 * });
 * ```
 */
export function createBusinessFlowHandler(config: BusinessFlowConfig) {
  const handlerConfig: LeadHandlerConfig = {
    onSubmit: async (data: LeadFormData): Promise<LeadApiResponse> => {
      // Remove token from data if present (already verified by reCAPTCHA handler)
      const { token, ...leadData } = data;

      // Prepare the request payload
      const payload = {
        ...leadData,
        sourceUrl: config.sourceUrl,
      };

      try {
        const response = await fetch(`${config.apiUrl}/api/Marketing/MarketingLead/Lead`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': config.apiKey,
          },
          body: JSON.stringify(payload),
        });

        const responseText = await response.text();
        let result;

        try {
          result = JSON.parse(responseText);
        } catch {
          // If response isn't JSON, create a response object
          result = {
            success: response.ok,
            message: response.ok ? 'Lead submitted successfully' : responseText || 'Unknown error',
          };
        }

        if (!response.ok) {
          return {
            success: false,
            message: result.message || `HTTP ${response.status}: ${response.statusText}`,
            data: result,
          };
        }

        return {
          success: true,
          message: result.message || 'Lead submitted successfully',
          id: result.id,
          data: result,
        };
      } catch (error) {
        console.error('BusinessFlow API error:', error);
        return {
          success: false,
          message: error instanceof Error ? error.message : 'Network error occurred',
        };
      }
    },

    // Configure reCAPTCHA if provided
    recaptcha: config.recaptchaSecret ? {
      secretKey: config.recaptchaSecret,
      minimumScore: config.minimumScore ?? 0.5,
    } : undefined,

    // Pass through callbacks
    onSuccess: config.onSuccess,
    onError: config.onError,
  };

  return createLeadHandler(handlerConfig);
}

/**
 * Simplified BusinessFlow handler for basic use cases
 * Uses environment variables for configuration
 *
 * @example
 * ```typescript
 * // app/api/lead/route.ts
 * import { createSimpleBusinessFlowHandler } from '@businessflow/leads/server';
 *
 * export const POST = createSimpleBusinessFlowHandler();
 * ```
 *
 * Required environment variables:
 * - BUSINESS_FLOW_API_URL - Base API URL (e.g., https://api.businessflow.co.za)
 * - BUSINESS_FLOW_LEADS_API_KEY - Leads-specific API key (format: leadsapi_live_xxx)
 * - BUSINESS_FLOW_LEADS_DOMAIN - Your website domain (e.g., https://yoursite.com)
 * - RECAPTCHA_SECRET_KEY (optional) - Google reCAPTCHA v3 secret key
 * - NEXT_PUBLIC_RECAPTCHA_SITE_KEY (optional) - Google reCAPTCHA v3 site key
 */
export function createSimpleBusinessFlowHandler() {
  const apiUrl = process.env.BUSINESS_FLOW_API_URL;
  const apiKey = process.env.BUSINESS_FLOW_LEADS_API_KEY;
  const sourceUrl = process.env.BUSINESS_FLOW_LEADS_DOMAIN;

  if (!apiUrl) {
    throw new Error(
      'Missing required environment variable: BUSINESS_FLOW_API_URL\n' +
      'Set this to your BusinessFlow CRM API URL (e.g., https://api.businessflow.co.za)\n' +
      'Add to your .env file: BUSINESS_FLOW_API_URL=https://api.businessflow.co.za'
    );
  }

  if (!apiKey) {
    throw new Error(
      'Missing required environment variable: BUSINESS_FLOW_LEADS_API_KEY\n' +
      'Get your leads API key from: https://crm.businessflow.co.za/settings/api-keys\n' +
      'Add to your .env file: BUSINESS_FLOW_LEADS_API_KEY=leadsapi_live_xxx'
    );
  }

  if (!sourceUrl) {
    throw new Error(
      'Missing required environment variable: BUSINESS_FLOW_LEADS_DOMAIN\n' +
      'Set this to your marketing website URL (e.g., https://yoursite.com)\n' +
      'Add to your .env file: BUSINESS_FLOW_LEADS_DOMAIN=https://yoursite.com'
    );
  }

  return createBusinessFlowHandler({
    apiUrl,
    apiKey,
    sourceUrl,
    recaptchaSecret: process.env.RECAPTCHA_SECRET_KEY,
  });
}